'use client';

import type { StoreApi, UseBoundStore } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';

interface FaucetCooldownState {
  cooldownEndsAtByAddress: Record<string, number>;
  setCooldownEndsAt: (walletAddress: string, endsAtMs: number) => void;
}

const SAFE_STORAGE: StateStorage = {
  getItem: (name: string): string | null => {
    try {
      return globalThis.localStorage?.getItem(name) ?? null;
    } catch {
      // SILENT-CATCH: restricted storage must degrade to the in-memory cooldown.
      return null;
    }
  },
  setItem: (name: string, value: string): void => {
    try {
      globalThis.localStorage?.setItem(name, value);
    } catch {
      // SILENT-CATCH: restricted storage must not prevent the faucet from updating.
    }
  },
  removeItem: (name: string): void => {
    try {
      globalThis.localStorage?.removeItem(name);
    } catch {
      // SILENT-CATCH: symmetric with writes; the in-memory cooldown remains authoritative.
    }
  },
};

export const useFaucetCooldownStore: UseBoundStore<StoreApi<FaucetCooldownState>> =
  createTrackedStore<FaucetCooldownState>('useFaucetCooldownStore')(
    persist(
      (set) => ({
        cooldownEndsAtByAddress: {},
        setCooldownEndsAt: (walletAddress: string, endsAtMs: number): void => {
          set((state) => ({
            cooldownEndsAtByAddress: {
              ...state.cooldownEndsAtByAddress,
              [walletAddress]: endsAtMs,
            },
          }));
        },
      }),
      {
        // Versioned: a new key drops cooldowns remembered for a retired faucet mint.
        name: 'morfi-faucet-cooldowns-v2',
        partialize: (
          state: FaucetCooldownState,
        ): Pick<FaucetCooldownState, 'cooldownEndsAtByAddress'> => ({
          cooldownEndsAtByAddress: state.cooldownEndsAtByAddress,
        }),
        storage: createJSONStorage(() => SAFE_STORAGE),
      },
    ),
  );
